import React, { useState } from 'react';
import style from './Contact.scss.json';
import './Contact.scss';
import Header from '../components/molecules/Header';
import { DefaultLayout } from '../layout/DefaultLayout';
import { Container } from '../layout/Container';
import { TextField } from 'lincd-input/lib/components/TextField';
import { useForm } from 'react-hook-form';
import { Button } from 'lincd-mui-base/lib/components/Button';
import { ContactForm } from '../types/form';
import { Server } from 'lincd-server-utils/lib/utils/Server';
import { packageName } from '../package';
import { useTranslate } from '@tolgee/react';
import { cl } from 'lincd/lib/utils/ClassNames';

const SUBJECT_OPTIONS = [
  { label: 'Contact the Team', value: 'contact_team' },
  { label: 'Feature Suggestion', value: 'feature_suggestion' },
  { label: 'Technical Support', value: 'technical_support' },
  { label: 'Data Deletion', value: 'data_deletion' },
  {
    label: 'Report User or Content / Block User',
    value: 'report_user_or_content',
  },
  { label: 'Report a Bug🐛', value: 'report_bug' },
  { label: 'Translation Suggestion', value: 'translation_suggestion' },
];

const Contact = () => {
  const [isLoading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  }>();

  let { t } = useTranslate();
  let prefix = 'contact';

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm<ContactForm>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      email: '',
      subject: '',
      message: '',
    },
  });

  // watch all fields to determine if the form is filled
  const watchedFields = watch();
  const isFormFilled =
    watchedFields.name &&
    watchedFields.email &&
    watchedFields.subject &&
    watchedFields.message;

  // disable submit button if form is not filled or if loading
  const isSubmitDisabled = !isFormFilled || isLoading;

  const onSubmit = async (data: ContactForm) => {
    setLoading(true);
    try {
      const response = await Server.call(
        packageName,
        'submitContactForm',
        data,
      );

      if (!response.success) {
        setMessage({
          type: 'error',
          text:
            response.error ||
            t(
              prefix + '.submit.error',
              'An error occurred while submitting the form. Please try again later.',
            ),
        });
      }

      setMessage({
        type: 'success',
        text: t(
          prefix + '.submit.success',
          'Thank you for your message! We will get back to you soon.',
        ),
      });

      // reset the form fields after successful submission
      reset();
    } catch (error) {
      console.error('Error submitting form:', error);
      setMessage({
        type: 'error',
        text: t(
          prefix + '.submit.error',
          'An error occurred while submitting the form. Please try again later.',
        ),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <DefaultLayout>
      <Header
        title="Contact"
        inverted={true}
        fixed={true}
        backButton={true}
        isFullWidth
      />
      <Container>
        {/* <Spinner active={isLoading} centered={true} /> */}
        {/* <iframe
          className={style.iframe}
          src="https://embed.wotnot.io/6yraDLPodMoq0621384426165jH5Qmhz/bot/5KW9jtAhavMJ062205947117YWmTauYL?display_header=false&history_retention=false"
          frameBorder="0"
        ></iframe> */}

        <div className={style.wrapper}>
          <form onSubmit={handleSubmit(onSubmit)} className={style.form}>
            <div className={style.field}>
              <label htmlFor="name">{t(prefix + '.name', 'Name')}*</label>
              <TextField
                id="name"
                {...register('name', {
                  required: t(prefix + '.nameRequired', 'Name is required'),
                })}
              />
              {errors.name && (
                <span className={style.error}>{errors.name.message}</span>
              )}
            </div>
            <div className={style.field}>
              <label htmlFor="email">{t(prefix + '.email', 'Email')}*</label>
              <TextField
                id="email"
                type="email"
                {...register('email', {
                  required: t(prefix + '.emailRequired', 'Email is required'),
                  pattern: {
                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                    message: t(
                      prefix + '.emailInvalid',
                      'Please enter a valid email address.',
                    ),
                  },
                })}
              />
              {errors.email && (
                <span className={style.error}>{errors.email.message}</span>
              )}
            </div>
            <div className={style.field}>
              <label htmlFor="subject">
                {t(prefix + '.subject', 'Subject')}*
              </label>
              <select
                id="subject"
                {...register('subject', {
                  required: t(
                    prefix + '.subjectRequired',
                    'Subject is required',
                  ),
                })}
              >
                <option value="">
                  {t(prefix + '.subjectSelect', 'Select an option')}
                </option>
                {SUBJECT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(`${prefix}.subject.${option.value}`, option.label)}
                  </option>
                ))}
              </select>
              {errors.subject && (
                <span className={style.error}>{errors.subject.message}</span>
              )}
            </div>
            <div className={style.field}>
              <label htmlFor="message">
                {t(prefix + '.message', 'Message')}*
              </label>
              <textarea
                id="message"
                {...register('message', {
                  required: t(
                    prefix + '.messageRequired',
                    'Message is required',
                  ),
                })}
              />
              {errors.message && (
                <span className={style.error}>{errors.message.message}</span>
              )}
            </div>
            <div>
              <Button type="submit" disabled={isSubmitDisabled}>
                {isLoading
                  ? t(prefix + '.submitting', 'Submitting...')
                  : t(prefix + '.submit', 'Submit')}
              </Button>
            </div>
            {message && (
              <div>
                <p
                  className={cl(
                    style.message,
                    message?.type === 'success' ? style.success : style.error,
                  )}
                >
                  {message.text}
                </p>
              </div>
            )}
          </form>
        </div>
      </Container>
    </DefaultLayout>
  );
};

export default Contact;
